import { useNavigate } from "react-router-dom";
import { Area, Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { CheckCircle2, XCircle } from "lucide-react";
import { useData } from "@/context/DataContext";
import { KpiCard, Panel, PageHeader, ChartTip } from "@/components/Widgets";
import { cut, fmt, fmtCr, pct, sum, last, PALETTE } from "@/lib/api";
import { entityGroup } from "@/lib/metrics";

const axis = { tickLine: false, axisLine: false };

export default function Overview() {
  const { data, horizon, filters } = useData();
  const fe = data.model.entities.find((x) => x.code === filters.entity);
  const { model } = data;
  const g = fe ? entityGroup(data.group, fe) : data.group;
  const years = cut(model.years, horizon);
  const s = (k) => cut(g[k], horizon);
  const rev = s("income");
  const ebitda = s("ebitda");
  const closing = s("closing");
  const debt = s("debt_total");
  const minClose = Math.min(...closing);
  const cagr = rev[0] > 0 && rev.length > 1 ? (last(rev) / rev[0]) ** (1 / (rev.length - 1)) - 1 : null;
  const series = years.map((y, i) => ({
    y, revenue: rev[i], ebitda: ebitda[i], margin: rev[i] ? ebitda[i] / rev[i] : 0, ncf: s("grand_total")[i],
    closing: closing[i], debt: debt[i], drawdown: s("drawdown")[i], repayment: s("repayment")[i],
  }));
  const ents = fe ? [fe] : model.entities.filter((e) => e.switch);
  const central = cut(g.inter_unit_funding.map((v, i) => v + g.central_expense[i]), horizon);
  const others = s("other_entities_ncf");
  const stack = years.map((y, i) => {
    const row = { y, Others: others[i], Central: central[i] };
    ents.forEach((e) => { row[e.short] = cut(e.series.ncf, horizon)[i] * e.switch; });
    return row;
  });
  return (
    <div data-testid="overview-page">
      <PageHeader kicker={`${data.version.name} · FY ${years[0]} – FY ${last(years)}`} title={fe ? `Group Financial Overview - ${fe.short}` : "Group Financial Overview"} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiCard testid="kpi-revenue" label={`Revenue FY ${years[0]}`} value={fmtCr(rev[0])} sub={cagr != null ? `${pct(cagr)} CAGR to FY ${last(years)}` : ""} />
        <KpiCard testid="kpi-ebitda" delay={60} label="EBITDA (horizon)" value={fmtCr(sum(ebitda))} sub={`Avg margin ${pct(sum(ebitda) / sum(rev))}`} tone="blue" />
        <KpiCard testid="kpi-ncf" delay={120} label={fe ? `${fe.short} NCF (horizon)` : "Group NCF (horizon)"} value={fmtCr(sum(s("grand_total")))} tone={sum(s("grand_total")) >= 0 ? "pos" : "neg"} sub={fe ? "Entity sheet net cash flow" : "After central funding & HO costs"} />
        <KpiCard testid="kpi-closing" delay={180} label={`Closing funds FY ${last(years)}`} value={fmtCr(last(closing))} tone={last(closing) >= 0 ? "pos" : "neg"} sub={`Low ${fmtCr(minClose)} in FY ${years[closing.indexOf(minClose)]}`} />
        <KpiCard testid="kpi-debt" delay={240} label={`Debt FY ${last(years)}`} value={fmtCr(last(debt))} tone="amber" sub={`Peak ${fmtCr(Math.max(...debt))}`} />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel title="Revenue & EBITDA" subtitle="Switched-on entities, EBITDA margin on right axis" testid="chart-revenue-ebitda">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={series}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="y" {...axis} />
              <YAxis yAxisId="l" {...axis} tickFormatter={(v) => fmt(v)} width={60} />
              <YAxis yAxisId="r" orientation="right" {...axis} tickFormatter={(v) => `${Math.round(v * 100)}%`} width={40} />
              <Tooltip content={<ChartTip percentKeys={["margin"]} />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="l" dataKey="revenue" name="Revenue" fill="#3B82F6" radius={[3, 3, 0, 0]} />
              <Bar yAxisId="l" dataKey="ebitda" name="EBITDA" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              <Line yAxisId="r" dataKey="margin" name="EBITDA %" stroke="#10B981" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title={fe ? `${fe.short} Net Cash Flow & Group Closing Funds` : "Group Net Cash Flow & Closing Funds"} subtitle={fe ? "Entity NCF bars; closing funds are held centrally (group)" : "NCF sheet grand total; closing = opening + NCF"} testid="chart-ncf-closing">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={series}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="y" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmt(v)} width={60} />
              <Tooltip content={<ChartTip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke="#475569" />
              <Bar dataKey="ncf" name="Group NCF" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              <Line dataKey="closing" name="Closing funds" stroke="#10B981" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Net Cash Flow by Entity" subtitle="Contribution to group NCF (switch applied)" testid="chart-ncf-by-entity">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={stack} stackOffset="sign">
              <CartesianGrid vertical={false} />
              <XAxis dataKey="y" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmt(v)} width={60} />
              <Tooltip content={<ChartTip />} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <ReferenceLine y={0} stroke="#475569" />
              {ents.map((e, i) => <Bar key={e.code} dataKey={e.short} stackId="a" fill={PALETTE[i % PALETTE.length]} />)}
              <Bar dataKey="Others" name="Other corporates" stackId="a" fill="#64748B" />
              <Bar dataKey="Central" name="Central (funding + HO)" stackId="a" fill="#CBD5E1" />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Debt Profile" subtitle="Loans sheet grand total with unit drawdowns & repayments" testid="chart-debt-profile">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={series}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="y" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmt(v)} width={60} />
              <Tooltip content={<ChartTip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area dataKey="debt" name="Closing debt" stroke="#F43F5E" fill="#F43F5E" fillOpacity={0.12} strokeWidth={2} />
              <Bar dataKey="drawdown" name="Drawdown" fill="#3B82F6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="repayment" name="Repayment" fill="#10B981" radius={[0, 0, 3, 3]} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <EntitySnapshot className="xl:col-span-2" />
        <ChecksPanel checks={g.checks} />
      </div>
    </div>
  );
}

const EntitySnapshot = ({ className }) => {
  const { data, horizon, filters } = useData();
  const nav = useNavigate();
  const list = data.model.entities.filter((e) => !filters.entity || e.code === filters.entity);
  return (
    <Panel title="Entity Snapshot" subtitle="Totals across selected horizon · click a row to drill down" className={className} testid="entity-snapshot">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="py-2 pr-3">Entity</th><th className="py-2 pr-3 text-right">Revenue</th><th className="py-2 pr-3 text-right">EBITDA</th>
              <th className="py-2 pr-3 text-right">Margin</th><th className="py-2 pr-3 text-right">Project capex</th><th className="py-2 text-right">NCF</th>
            </tr>
          </thead>
          <tbody>
            {list.map((e) => {
              const r = sum(cut(e.series.income, horizon));
              const eb = sum(cut(e.series.ebitda, horizon));
              const n = sum(cut(e.series.ncf, horizon));
              return (
                <tr key={e.code} onClick={() => nav(`/entities/${e.code}`)} data-testid={`snapshot-row-${e.code}`}
                  className="cursor-pointer border-t border-slate-800 transition-colors hover:bg-slate-800/40">
                  <td className="py-2.5 pr-3">
                    <div className="text-slate-100">{e.name}</div>
                    <div className="text-[11px] text-slate-500">{e.category}{!e.switch && " · switched off in NCF"}</div>
                  </td>
                  <td className="num py-2.5 pr-3 text-right">{fmt(r)}</td>
                  <td className="num py-2.5 pr-3 text-right">{fmt(eb)}</td>
                  <td className="num py-2.5 pr-3 text-right text-slate-400">{r ? pct(eb / r) : "–"}</td>
                  <td className="num py-2.5 pr-3 text-right">{fmt(sum(cut(e.series.project_capex, horizon)))}</td>
                  <td className={`num py-2.5 text-right ${n < 0 ? "text-rose-400" : "text-emerald-400"}`}>{fmt(n)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

const ChecksPanel = ({ checks }) => (
  <Panel title="Model Integrity" subtitle="Reconciliation of parsed values against workbook totals" testid="checks-panel">
    <ul className="space-y-2.5">
      {checks.map((c) => (
        <li key={c.name} className="flex items-start gap-2 text-sm" data-testid="integrity-check">
          {c.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />}
          <span className={c.ok ? "text-slate-300" : "text-rose-300"}>{c.name}</span>
        </li>
      ))}
    </ul>
  </Panel>
);
